/**
 * LeftShield Portal - Merge Gate Settings Page
 * Configure enforcement policies, framework overrides, and merge gate defaults.
 *
 * Route: /settings/merge-gate | Nav: Settings > Merge Gate
 *
 * The Framework Overrides grid is migrated onto the List & Grid Standard — see
 * `.specify/journals/list-grid-adoption-journal.md` and the reference migration
 * `src/pages/ApiKeysPage.tsx`. The Global Enforcement and Merge Gate Policies
 * sections are non-list content and stay as fixed siblings outside the scroll frame.
 */
import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Title2,
  Body1,
  Body2,
  Text,
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  Switch,
  Dropdown,
  Option,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ShieldCheckmarkRegular,
  SettingsRegular,
  SaveRegular,
  ArrowResetRegular,
  BranchForkRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  enforcementApi,
  mergeGateApi,
  type EnforcementConfig,
  type UpdateEnforcementConfigRequest,
  type ViolationAction,
  type ControlSeverity,
  type PolicyStage,
  type FrameworkOverride,
} from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import type { MergeGatePolicy } from '../types';
import { PageHeader } from '../components/common/PageHeader';
import { useAuth } from '../features/authentication';
import {
  ListPageLayout,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';

const VIOLATION_ACTIONS: ViolationAction[] = ['Allow', 'Warn', 'Block'];
const SEVERITY_LEVELS: ControlSeverity[] = ['Info', 'Low', 'Medium', 'High', 'Critical'];
const POLICY_STAGES: PolicyStage[] = ['Shadow', 'Canary', 'Production'];

/**
 * Roles allowed to edit a per-framework enforcement override — mirrors
 * `McpConfigurationController.UpdateFrameworkOverride`'s `RequireAnyRole(...)`.
 */
const OVERRIDE_MANAGER_ROLES = ['System.Admin', 'Organization.Admin', 'TenantAdmin', 'ComplianceManager'];

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalXXXL,
  },
  overridesCard: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

const getActionColor = (action: string): 'success' | 'warning' | 'danger' | 'informative' => {
  switch (action) {
    case 'Allow':
      return 'success';
    case 'Warn':
      return 'warning';
    case 'Block':
      return 'danger';
    default:
      return 'informative';
  }
};

const getStageColor = (stage: string): 'informative' | 'warning' | 'success' => {
  switch (stage) {
    case 'Shadow':
      return 'informative';
    case 'Canary':
      return 'warning';
    case 'Production':
      return 'success';
    default:
      return 'informative';
  }
};

const getStageDescription = (stage: string): string => {
  switch (stage) {
    case 'Shadow':
      return 'Log violations but never block — safe rollout for new policies';
    case 'Canary':
      return 'Warn but never block — gradual enforcement rollout';
    case 'Production':
      return 'Full enforcement — violations may block code generation';
    default:
      return '';
  }
};

/** Wraps the framework-overrides list (nested in the unpaged enforcement config response) as a
 *  `PagedResult` — `useAdaptiveGridData` then applies search/sort/paging in memory (client mode),
 *  since the override list is never large enough to warrant a dedicated paged endpoint. */
async function fetchFrameworkOverridesPage(params: ListQueryParams): Promise<PagedResult<FrameworkOverride>> {
  const config = await enforcementApi.getConfig();
  const items = config.frameworkOverrides ?? [];
  return {
    items,
    totalCount: items.length,
    pageNumber: 1,
    pageSize: params.pageSize,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  };
}

export const MergeGateSettingsPage: FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canManageOverrides = user?.roles?.some((role) => OVERRIDE_MANAGER_ROLES.includes(role)) ?? false;

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState<string | null>(null);

  // Local form state
  const [isEnabled, setIsEnabled] = useState(false);
  const [defaultAction, setDefaultAction] = useState<ViolationAction>('Warn');
  const [minimumSeverity, setMinimumSeverity] = useState<ControlSeverity>('Medium');
  const [strictMode, setStrictMode] = useState(false);
  const [stage, setStage] = useState<PolicyStage>('Production');

  // Fetch enforcement config
  const { data: config, isLoading: configLoading } = useQuery<EnforcementConfig>({
    queryKey: ['mergeGate', 'config'],
    queryFn: () => enforcementApi.getConfig(),
  });

  // Fetch merge gate policies
  const { data: policies, isLoading: policiesLoading } = useQuery<MergeGatePolicy[]>({
    queryKey: ['mergeGate', 'policies'],
    queryFn: () => mergeGateApi.getPolicies(),
  });

  // Initialize form state from API data
  useEffect(() => {
    if (config) {
      setIsEnabled(config.isEnabled);
      setDefaultAction(config.defaultAction);
      setMinimumSeverity(config.minimumSeverity);
      setStrictMode(config.strictMode);
      setStage(config.stage ?? 'Production');
    }
  }, [config]);

  // Save config mutation
  const saveMutation = useMutation({
    mutationFn: (request: UpdateEnforcementConfigRequest) => enforcementApi.updateConfig(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mergeGate'] });
      setShowSuccess(true);
      setShowError(null);
      setTimeout(() => setShowSuccess(false), 3000);
    },
    onError: (error: Error) => {
      setShowError(error.message);
      setTimeout(() => setShowError(null), 5000);
    },
  });

  // Update framework override mutation
  const overrideMutation = useMutation({
    mutationFn: (override: { frameworkCode: string; action: ViolationAction; isEnabled: boolean }) =>
      enforcementApi.updateFrameworkOverride(override),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mergeGate', 'config'] });
      queryClient.invalidateQueries({ queryKey: ['mergeGate', 'frameworkOverrides'] });
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      isEnabled,
      defaultAction,
      minimumSeverity,
      strictMode,
      stage,
    });
  };

  const handleReset = () => {
    if (config) {
      setIsEnabled(config.isEnabled);
      setDefaultAction(config.defaultAction);
      setMinimumSeverity(config.minimumSeverity);
      setStrictMode(config.strictMode);
      setStage(config.stage ?? 'Production');
    }
  };

  // --- Framework Overrides — List & Grid Standard ---------------------------------------

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'frameworkName:asc' });

  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = [{ field: sortField, dir: sortDir }];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const overridesGrid = useAdaptiveGridData<FrameworkOverride>({
    queryKeyBase: ['mergeGate', 'frameworkOverrides'],
    fetchPage: fetchFrameworkOverridesPage,
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['frameworkName', 'frameworkCode'],
    sortState,
  });

  const {
    items: frameworkOverrides,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading: overridesLoading,
    isFetching: overridesFetching,
    isError: overridesError,
    refetch: refetchOverrides,
  } = overridesGrid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after a search/edit.
  useEffect(() => {
    if (!overridesLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [overridesLoading, state.page, totalPages, setPage]);

  const overridesColumnCount = canManageOverrides ? 4 : 3;

  let overridesListBody: ReactNode;
  if (overridesLoading) {
    overridesListBody = <LoadingSkeleton rows={4} columns={overridesColumnCount} />;
  } else if (overridesError) {
    overridesListBody = <ErrorState message="Unable to load framework overrides." onRetry={refetchOverrides} />;
  } else if (frameworkOverrides.length === 0) {
    overridesListBody = (
      <EmptyState
        icon={<SettingsRegular />}
        title="No Framework Overrides"
        description="Enable frameworks in the Compliance settings to add overrides."
      />
    );
  } else {
    overridesListBody = (
      <ListLoadingOverlay busy={overridesFetching && !overridesLoading}>
        <Table aria-label="Framework overrides">
          <TableHeader>
            <TableRow>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'frameworkName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('frameworkName')}
                onClick={() => toggleSort('frameworkName')}
                style={{ width: '100%' }}
              >
                Framework
              </TableHeaderCell>
              <TableHeaderCell>Current</TableHeaderCell>
              <TableHeaderCell>Enabled</TableHeaderCell>
              {canManageOverrides && (
                <TableHeaderCell style={{ width: actionsColumnWidth(2) }}>Actions</TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {frameworkOverrides.map((item) => (
              <TableRow key={item.frameworkCode}>
                <TableCell>
                  <div>
                    <Body1 style={{ fontWeight: 500 }}>{item.frameworkName}</Body1>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{item.frameworkCode}</Body2>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge appearance="filled" color={getActionColor(item.action)}>
                    {item.action}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={item.isEnabled ? 'success' : 'informative'}>
                    {item.isEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </TableCell>
                {canManageOverrides && (
                  <TableCell>
                    {/* Hidden-not-disabled: the edit affordance (dropdown + switch) that calls the
                        protected `updateFrameworkOverride` mutation is omitted entirely for
                        non-managers — the Current/Enabled badges above still show them the state. */}
                    <div className={styles.actionsCell}>
                      <Dropdown
                        value={item.action}
                        onOptionSelect={(_, data) => {
                          overrideMutation.mutate({
                            frameworkCode: item.frameworkCode,
                            action: data.optionValue as ViolationAction,
                            isEnabled: item.isEnabled,
                          });
                        }}
                        style={{ minWidth: '7.5rem' }}
                        aria-label={`Change enforcement action for ${item.frameworkName}`}
                      >
                        {VIOLATION_ACTIONS.map((action) => (
                          <Option key={action} value={action}>
                            {action}
                          </Option>
                        ))}
                      </Dropdown>
                      <Switch
                        checked={item.isEnabled}
                        onChange={(_, data) => {
                          overrideMutation.mutate({
                            frameworkCode: item.frameworkCode,
                            action: item.action,
                            isEnabled: data.checked,
                          });
                        }}
                        aria-label={`Toggle override for ${item.frameworkName}`}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  // --- Merge Gate Policies — unchanged (non-mutating, out of scope for this migration) ----

  const policyColumns: TableColumnDefinition<MergeGatePolicy>[] = [
    createTableColumn<MergeGatePolicy>({
      columnId: 'repository',
      compare: (a, b) => a.repositoryName.localeCompare(b.repositoryName),
      renderHeaderCell: () => 'Repo',
      renderCell: (item) => (
        <Body1 style={{ fontWeight: 500 }}>{item.repositoryName}</Body1>
      ),
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'blockCritical',
      renderHeaderCell: () => 'Block on Critical',
      renderCell: (item) => (
        <Badge appearance="tint" color={item.blockOnCritical ? 'danger' : 'informative'}>
          {item.blockOnCritical ? 'Yes' : 'No'}
        </Badge>
      ),
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'blockHigh',
      renderHeaderCell: () => 'Block on High',
      renderCell: (item) => (
        <Badge appearance="tint" color={item.blockOnHigh ? 'danger' : 'informative'}>
          {item.blockOnHigh ? 'Yes' : 'No'}
        </Badge>
      ),
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'requireEvidence',
      renderHeaderCell: () => 'Require Evidence',
      renderCell: (item) => (
        <Badge appearance="tint" color={item.requireEvidence ? 'success' : 'informative'}>
          {item.requireEvidence ? 'Yes' : 'No'}
        </Badge>
      ),
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'minScore',
      renderHeaderCell: () => 'Min Score',
      renderCell: (item) => (
        <Body2>{item.minimumScore != null ? `${item.minimumScore}%` : '-'}</Body2>
      ),
    }),
    createTableColumn<MergeGatePolicy>({
      columnId: 'enabled',
      renderHeaderCell: () => 'Enabled',
      renderCell: (item) => (
        <Badge appearance="filled" color={item.isEnabled ? 'success' : 'informative'}>
          {item.isEnabled ? 'Active' : 'Disabled'}
        </Badge>
      ),
    }),
  ];

  if (configLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading merge gate settings..." />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Merge Gate Settings"
        subtitle="Configure merge gate enforcement rules and thresholds"
        actions={
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
            <Button
              appearance="outline"
              icon={<ArrowResetRegular />}
              onClick={handleReset}
              disabled={saveMutation.isPending}
            >
              Reset
            </Button>
            <Button
              appearance="primary"
              icon={<SaveRegular />}
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        }
      />

      {/* Status Messages */}
      {showSuccess && (
        <MessageBar intent="success">
          <MessageBarBody>Merge gate settings saved successfully.</MessageBarBody>
        </MessageBar>
      )}
      {showError && (
        <MessageBar intent="error">
          <MessageBarBody>Error saving settings: {showError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Global Enforcement Settings — non-list content, fixed sibling outside the scroll frame */}
      <Card>
        <CardHeader
          image={<ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
          header={<Title2>Global Enforcement</Title2>}
          description={<Body2>Configure global enforcement behavior for merge gates</Body2>}
        />
        <div className={ps.cardBody}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: `${tokens.spacingVerticalS} 0`,
            }}
          >
            <div>
              <Text weight="semibold">Enforcement Enabled</Text>
              <Text
                size={200}
                style={{ display: 'block', color: tokens.colorNeutralForeground3 }}
              >
                When enabled, merge gates will enforce compliance policies on pull requests
              </Text>
            </div>
            <Switch
              checked={isEnabled}
              onChange={(_, data) => setIsEnabled(data.checked)}
            />
          </div>

          {/* Deployment Stage */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: `${tokens.spacingVerticalS} 0`,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                <Text weight="semibold">Deployment Stage</Text>
                <Badge appearance="filled" color={getStageColor(stage)}>{stage}</Badge>
              </div>
              <Text
                size={200}
                style={{ display: 'block', color: tokens.colorNeutralForeground3 }}
              >
                {getStageDescription(stage)}
              </Text>
            </div>
            <Dropdown
              value={stage}
              onOptionSelect={(_, data) =>
                setStage(data.optionValue as PolicyStage)
              }
              style={{ minWidth: '160px' }}
            >
              {POLICY_STAGES.map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Dropdown>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: tokens.spacingHorizontalXL,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
              <Text weight="semibold">Default Action</Text>
              <Text
                size={200}
                style={{ color: tokens.colorNeutralForeground3 }}
              >
                Action taken when a violation is detected
              </Text>
              <Dropdown
                value={defaultAction}
                onOptionSelect={(_, data) =>
                  setDefaultAction(data.optionValue as ViolationAction)
                }
              >
                {VIOLATION_ACTIONS.map((action) => (
                  <Option key={action} value={action}>
                    {action}
                  </Option>
                ))}
              </Dropdown>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
              <Text weight="semibold">Minimum Severity</Text>
              <Text
                size={200}
                style={{ color: tokens.colorNeutralForeground3 }}
              >
                Only enforce on findings at or above this severity
              </Text>
              <Dropdown
                value={minimumSeverity}
                onOptionSelect={(_, data) =>
                  setMinimumSeverity(data.optionValue as ControlSeverity)
                }
              >
                {SEVERITY_LEVELS.map((level) => (
                  <Option key={level} value={level}>
                    {level}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: `${tokens.spacingVerticalS} 0`,
            }}
          >
            <div>
              <Text weight="semibold">Strict Mode</Text>
              <Text
                size={200}
                style={{ display: 'block', color: tokens.colorNeutralForeground3 }}
              >
                Block all merges when any violation is present, regardless of severity
              </Text>
            </div>
            <Switch
              checked={strictMode}
              onChange={(_, data) => setStrictMode(data.checked)}
            />
          </div>
        </div>
      </Card>

      {/* Framework Overrides — migrated onto the List & Grid Standard (ListPageLayout + Table +
          Pager, PAGE_SIZE=25 via useAdaptiveGridData, useListUrlState, four-state discipline) */}
      <Card className={styles.overridesCard}>
        <CardHeader
          image={<SettingsRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
          header={<Title2>Framework Overrides</Title2>}
          description={<Body2>Override enforcement action per framework</Body2>}
        />
        <div className={styles.listSection}>
          <ListPageLayout
            toolbar={
              <ListToolbar
                search={state.search}
                onSearchChange={setSearch}
                searchPlaceholder="Search frameworks…"
                onRefresh={refetchOverrides}
                isRefreshing={overridesFetching}
                testIdPrefix="framework-overrides"
              />
            }
            footer={
              <Pager
                page={state.page}
                totalPages={totalPages}
                hasPreviousPage={hasPreviousPage}
                hasNextPage={hasNextPage}
                totalCount={totalCount}
                onPageChange={setPage}
                testId="framework-overrides-pager"
              />
            }
          >
            {overridesListBody}
          </ListPageLayout>
        </div>
      </Card>

      {/* Merge Gate Policies — non-list content, fixed sibling outside the scroll frame.
          Read-only display (no per-row mutation) — left on the existing Fluent DataGrid;
          out of scope for this migration (see journal). */}
      <Card>
        <CardHeader
          image={<BranchForkRegular style={{ fontSize: '1.5rem', color: tokens.colorBrandForeground1 }} />}
          header={<Title2>Merge Gate Policies</Title2>}
          description={<Body2>Default policies for repository merge gates</Body2>}
        />
        {policiesLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
            <Spinner size="medium" label="Loading policies..." />
          </div>
        ) : policies && policies.length > 0 ? (
          <DataGrid items={policies} columns={policyColumns} sortable>
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<MergeGatePolicy>>
              {({ item, rowId }) => (
                <DataGridRow<MergeGatePolicy> key={rowId}>
                  {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        ) : (
          <Body1 className={ps.emptyState}>
            No merge gate policies configured. Register webhooks to create repository policies.
          </Body1>
        )}
      </Card>
    </div>
  );
};
